// @givmo/board — a director's to-do list, on every page about their board (CBG-UX7-WEB-DIR; design
// stop design-268's §4.1, §4.2, §4.5, §4.6, §6.1 and §7, frames d1–d5).
//
// Mounted ONCE, by the director's shell (`DirectorShell`), and drawn only on a page about one board the
// signed-in account holds a seat on (`?org=` naming a board `/me` lists): the multi-seat home and the
// account's own pages carry none (§7). Its To do reads two reads the director pages already make,
// under the keys those pages read them by, so an act anywhere that clears an item clears it here at
// once:
//   * the board's attention read, the retired "Waiting on you" card's source (D-CBG-35 (2): the card
//     becomes this list, its words and its Open carried over unchanged);
//   * the member's own getting-started list, the new member checklist, item by item (none for a seat
//     on committees only: the list is the board's).
// Its Completed reads the new completed read, under the attention read's own key, so every act that
// refreshes the one refreshes the other (§6.3). A director surface calls director operations only (the
// capacity rail). All three read again when the window regains focus; nothing polls.
//
// AN EMAILED LINK STILL SHOWS ITS ITEM (§4.6): something the list hasn't shown on this device opens it
// on its own, once (`todo-store.tsx`), so a consent just circulated is in view on the page its email
// opens, as it was in the card at the page's top.
//
// A FIRST VISIT OPENS IT, EMPTY OR NOT (CBG-UX10, D-CBG-40): the board is the list's place, so the
// first time this account opens one of the board's pages on this device, the list opens though
// nothing waits, and its empty state says what the list is and what will show up on it.

import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError, type OrgBoardDirectorAttentionOut } from '@givmo/api-client';
import { toast } from '@givmo/design-system';
import { usePathname, useSearch } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardSearch,
  directorOrgOf,
  seatCommitteeNames,
} from '../../director/director-board';
import { useBoardDirectorHost } from '../../director/director-host';
import { directorBoardFor, useDirectorMe } from '../../director/director-me';
import { useDirectorReadsMayFire } from '../../director/read-gate';
import { isCommitteeOnly, type DirectorBoard } from '../../director/director-wire';
import { todayAsDateInput } from '../../lib/display';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { extractOwnList } from '../onboarding/onboarding-decode';
import { identitiesOf } from '../todo/todo-model';
import { ToDoPanel } from '../todo/todo-panel';
import { useToDoPresence, useToDoStore } from '../todo/todo-store';
import { directorGettingStartedKey } from './director-getting-started-page';
import { directorList } from './director-todo-list';
import { useMinutesWaitingOpen } from './minutes-waiting-open';

/** The attention read's key, every director page's. */
function attentionKey(orgRef: string) {
  return ['board-director-attention', orgRef] as const;
}

/** The completed read's key: beneath the attention read's, so it refreshes whenever that does. */
export function directorCompletedKey(orgRef: string) {
  return ['board-director-attention', orgRef, 'completed'] as const;
}

export function DirectorToDo() {
  const { account } = useBoardDirectorHost();
  const orgRef = directorOrgOf(useSearch());
  const meQuery = useDirectorMe();
  const board = directorBoardFor(meQuery.data, orgRef);
  if (account === null || orgRef === null || board === null) return null;
  return (
    <DirectorToDoForBoard
      orgRef={orgRef}
      board={board}
      account={account.uid ?? account.email ?? null}
    />
  );
}

function DirectorToDoForBoard({
  orgRef,
  board,
  account,
}: {
  orgRef: string;
  board: DirectorBoard;
  account: string | null;
}) {
  useToDoPresence('director');
  const operations = useBoardDirectorOperations();
  const queryClient = useQueryClient();
  const store = useToDoStore();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const committeeOnly = isCommitteeOnly(board);
  const minutesOpen = useMinutesWaitingOpen();

  const attentionQuery = useQuery({
    queryKey: attentionKey(orgRef),
    enabled: mayFire,
    retry: false,
    refetchOnWindowFocus: 'always',
    queryFn: async (): Promise<OrgBoardDirectorAttentionOut> => {
      const { data } = await operations.directorGetBoardDirectorAttention({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return data;
    },
  });
  const listQuery = useQuery({
    queryKey: directorGettingStartedKey(orgRef),
    enabled: mayFire && !committeeOnly,
    retry: false,
    refetchOnWindowFocus: 'always',
    queryFn: async () => {
      const { data } = await operations.directorGetOwnBoardGettingStarted({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractOwnList(data);
    },
  });
  const completedQuery = useQuery({
    queryKey: directorCompletedKey(orgRef),
    enabled: mayFire,
    retry: false,
    refetchOnWindowFocus: 'always',
    queryFn: async (): Promise<unknown> => {
      const { data } = await operations.directorGetBoardDirectorAttentionCompleted({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return data;
    },
  });
  // A step marked from the list: the member's own act, the same as their checklist page's.
  const markStep = useMutation({
    mutationFn: async (itemId: string) => {
      await operations.directorMarkOwnBoardGettingStartedStep({
        path: { org_ref: orgRef, item_id: itemId },
        body: { done_on: todayAsDateInput() },
        throwOnError: true,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: directorGettingStartedKey(orgRef) }),
    onError: (caught) =>
      toast.error(isGivmoError(caught) ? caught.message : 'We couldn’t mark it. Please try again.'),
  });

  // To do waits for both of its reads; if one fails, the other's items still show (§7).
  const attentionSettled = attentionQuery.isSuccess || attentionQuery.isError;
  const listSettled = committeeOnly || listQuery.isSuccess || listQuery.isError;
  const failed = attentionQuery.isError && (committeeOnly || listQuery.isError);
  const committeeSaid = !(committeeOnly && seatCommitteeNames(board).length === 1);
  const list = useMemo(
    () =>
      attentionSettled && listSettled && !failed
        ? directorList({
            orgRef,
            attention: attentionQuery.data,
            ownList: committeeOnly ? null : (listQuery.data ?? null),
            completed: completedQuery.data,
            committeeSaid,
          })
        : null,
    [
      attentionSettled,
      listSettled,
      failed,
      orgRef,
      attentionQuery.data,
      committeeOnly,
      listQuery.data,
      completedQuery.data,
      committeeSaid,
    ],
  );

  // Something new opens the list on arrival at one of the board's pages, never mid-page (the store's
  // rule): the page (and the board) is part of what it notices, and an arrival lasts until To do's
  // reads have answered there (the list mounts again with each page, and reads again once stale).
  // The board is its place: the first arrival in it opens the list, empty or not (D-CBG-40).
  const page = `${usePathname()}?org=${orgRef}`;
  const settled = !attentionQuery.isFetching && !listQuery.isFetching;
  const identities = list === null ? null : identitiesOf(list).join('\n');
  // A Board Ask's own page is that item, already in view (`notice`'s `inView`).
  const askId = /\/director\/asks\/([^/?]+)/.exec(page)?.[1];
  const inView =
    askId === undefined
      ? undefined
      : list?.items.find((item) => item.key === `board-ask-${askId}`)?.identities[0];
  useEffect(() => {
    if (identities === null) return;
    store?.notice(
      'director',
      account,
      identities.length === 0 ? [] : identities.split('\n'),
      page,
      settled,
      orgRef,
      inView,
    );
  }, [store, account, identities, page, settled, orgRef, inView]);

  const retry = () => {
    void attentionQuery.refetch();
    if (!committeeOnly) void listQuery.refetch();
    void completedQuery.refetch();
  };

  return (
    <>
      <ToDoPanel
        surface="director"
        account={account}
        list={list}
        failed={failed}
        completedFailed={completedQuery.isError}
        onRetry={retry}
        onMarkDone={(itemId) => markStep.mutate(itemId)}
        marking={markStep.isPending ? (markStep.variables ?? null) : null}
        // FIX74's Open is a choice from the list: what it brings counts as shown (the store's rule),
        // so its minutes open with the list as it was left. Several waiting go nowhere: the list
        // opens with each as its RC10 row. Nothing came of it: the choice is withdrawn.
        onOpenWaitingMinutes={() =>
          minutesOpen.open(orgRef, (shown) => {
            if (shown === 'list') store?.setOpen('director', account, true);
            else if (shown === 'nothing') store?.chose('director', false);
          })
        }
        openingWaitingMinutes={minutesOpen.working}
        checklistPage={{
          to: BOARD_DIRECTOR_PAGES.gettingStarted,
          search: directorBoardSearch(orgRef),
        }}
        // A seat for someone who isn't on the board owes its committees, never the board.
        emptyLine={committeeOnly ? co.todoEmpty(seatCommitteeNames(board)) : undefined}
        testId="todo"
      />
      {minutesOpen.dialog}
      {minutesOpen.offer}
    </>
  );
}
